
import { SFConnection } from 'common/sf-connector';
import { IModule } from 'interfaces/IModule.interface';
import { SFPackage, SFPackageVersion } from 'sf-app/sf-package';
import { IPastRun } from '../interfaces/IHistory.interface';
import { IApplicableModules } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from 'app-constants';


interface KeyValuePair {
    key : string;
    value : any;
}

interface User {
    userId: string;
    username: string;
    displayName: string;
    email: string;
}

export class CQMgmtService {

    corePackage?: IModule;
    installedPackages: SFPackage[];
    defaultValues: KeyValuePair[];
    currentUser?: User;

    lastFailedModule?: IApplicableModules;
    lastRun?:IPastRun;
    lastSkippedModule?: IApplicableModules;
    lastSkippedRun?:IPastRun;

    constructor() {
        this.installedPackages = [];
        this.defaultValues = [];
    }
    

    /**
    * Function that Processes records of installed packages and creates 
    * a mapping of package names to their versions.
    *  
    * This function takes a list of records representing installed packages and filters
    * out packages that meet the following criteria:
    * - The package belongs to specific namespaces associated with CQ (CQ_NAMESPACES).
    * - The package is one of the known packages defined in CQ_NON_NAMESPACE_PKGS.
    *
    * For eligible packages meeting these criteria, the function creates corresponding
    * `SFPackage` instances with versions extracted from the provided records. It then
    * adds the package names and versions to a `Map<string, SFPackage>` (installedPackagesMap)
    * and updates the `this.installedPackages` array with the created `SFPackage` instances.
    *
    * @param {any} records - Represents the array of objects of installed Packages.
    * @returns {Map<string, SFPackage>} A Map containing the names of installed packages
    * as keys and their corresponding `SFPackage` instances with extracted versions as values.
    */

    public addInstalledPackage(records: any): Map<string, SFPackage> {
        let installedPackagesMap = new Map<string, SFPackage>();
    
        for (let record of records) {
            let recVersion = record.SubscriberPackageVersion;
            let recPkg = record.SubscriberPackage;
            let nameSpace = recPkg.NamespacePrefix || "";
            let firstInstalledPackageId = record?.MinPackageVersionId;
            // add to installed packages list if we are from cq's namespaces or are cq's packages
            let belongsToCQNamespace = CQAppConstant.CQ_NAMESPACES.indexOf(nameSpace) !== -1;
            let belongsToKnownPkgs = CQAppConstant.CQ_NON_NAMESPACE_PKGS.find(pkg =>pkg.name === recPkg.Name && pkg.namespace === nameSpace) !== undefined;

            if (belongsToCQNamespace || belongsToKnownPkgs) {
                let pkg = new SFPackage(recPkg.Name,
                    new SFPackageVersion(recVersion.MajorVersion, recVersion.MinorVersion, recVersion.PatchVersion), firstInstalledPackageId);
                installedPackagesMap.set(recPkg.Name, pkg);
                // Refer to how-to-debug guide
                
                this.installedPackages.push(pkg);
            }
        }
    
        return installedPackagesMap;
    }
    
    /**
     * A cache that stores information about installed packages.
     * Each entry in the cache contains the package name and its version.
     */
    private cachedInstalledPackage: { name: string; version: SFPackageVersion, firstInstalledPackageId?: string }[] = [];

    /**
     * This method retrieves the cached list of installed packages.
     * @returns 
    */
    public getCachedInstalledPackages() {
        return this.cachedInstalledPackage;
    }

    /**
     * Retrieves a list of installed packages and their versions.
     * @returns {Array<{ name: string, version: SFPackageVersion, firstInstalledPackageId : string }>} An array of objects containing the name and version of each installed package.
    */
    public checkForInstalledPackagesAndVersions() {
        if (this.cachedInstalledPackage.length > 0) {
            return this.cachedInstalledPackage;
        }
        return this.buildInstalledPackageCache();
    }

    
    /**
     * Retrieves the list of installed packages with their names, versions, and the
     * first-installed package identifier. The method will return a cached result
     * if one is available to avoid unnecessary remote calls; otherwise it will
     * fetch installed packages from SFConnection, merge them into the service's
     * internal store via addInstalledPackage, build a lightweight array of package
     * descriptors, cache that array, and return it.
     * TODO: This function is a copy of checkForInstalledPackagesAndVersions() which is made to cache the installed packages for the rendering of the installed package list in the UI and later on it will be refactored once the conversion of the below tasks are completed.
     * https://compliancequest.atlassian.net/browse/SVCMGMT-2051
    */
   public async checkInstalledPackagesWithVersions() {
        if (this.cachedInstalledPackage.length > 0) {
            return this.cachedInstalledPackage;
        }
        this.addInstalledPackage(await SFConnection.getInstalledPackages());
        return this.buildInstalledPackageCache();
    }

    /**
     * Builds and caches the list of installed packages with their name, version,
     * and first installed package ID.
     *
     * This method:
     * - Iterates over `this.installedPackages` to create a simplified package info array.
     * - Updates `this.cachedInstalledPackage` for future quick access.
     *
     * @returns An array of objects containing { name, version, firstInstalledPackageId }.
     */
    private buildInstalledPackageCache() {
        let storeInstalledPackages = [];
        for (let i = 0; i < this.installedPackages.length; i++) {
            let packageInfo = {
                name: this.installedPackages[i].name,
                version: this.installedPackages[i].version,
                firstInstalledPackageId: this.installedPackages[i].firstInstalledPackageId!
            };
            storeInstalledPackages.push(packageInfo);
        }
        this.cachedInstalledPackage = storeInstalledPackages;
        return storeInstalledPackages;
    }

}

export const CQMgmtServiceProvider = new CQMgmtService();
